/**
 * Bark inspector (docs/ux/project-tab-spec.md §1.3): You said · Viko understood · the live
 * action tree (task → run → landing → build; steer → run; answer → text) · conversation ·
 * history · controls (add context, redirect, re-triage, cancel, undo). Opens from cached state
 * at once; live data streams in after.
 */
import type { BarkRecord } from '@viko/protocol'
import { Button } from '@viko/transcript-ui'
import { useMemo, useState } from 'react'
import {
  type AggContext,
  BARK_GLYPH_LABEL,
  barkAggregate,
  barkTree,
  canUndo,
  type TreeNode,
} from '../../lib/bark-aggregate.js'
import { historyTimeline, redirectPreview, retriageDiff } from '../../lib/bark-outcomes.js'
import type { BarkStore } from '../../lib/bark-store.js'
import { Conversation } from '../discuss/Conversation.js'
import { Ago, errorText, useToast } from '../ui.js'
import { BarkGlyph } from './BarkGlyph.js'
import { useAggContext, useBark } from './useBarks.js'

export interface BarkInspectorProps {
  projectId: string
  barkId: string
  /** Open another inspector: `task:<id>`, `run:<id>`, `bark:<id>`, `decision:<id>`. */
  onInspect: (key: string) => void
  /** Open straight on the redirect choices (the rail's Redirect / `r`). */
  initialPanel?: 'redirect'
}

const REDIRECTS = [
  ['Make it its own task', 'In the workstream Viko thinks fits'],
  ['Steer a different run', 'Pick another live run in this project'],
  ['Answer it, don’t act', 'A reply here, no code changes'],
  ['Just keep it as a note', 'Saved to the project, no work started'],
] as const

const TONE_CLASS: Record<NonNullable<TreeNode['tone']>, string> = {
  ok: 'text-status-ok',
  run: 'text-status-run',
  wait: 'text-status-wait',
  fail: 'text-status-fail',
  idle: 'text-text-3',
}

function Tree({ nodes, onInspect }: { nodes: TreeNode[]; onInspect: (k: string) => void }) {
  return (
    <ul className="m-0 grid list-none gap-1.5 border-l border-border-subtle pl-3">
      {nodes.map((n) => (
        <li key={n.key} className="grid gap-1" data-tree-node={n.kind}>
          <div className="flex flex-wrap items-baseline gap-2 text-[15px]">
            {n.open ? (
              <button
                type="button"
                className="text-left text-accent-text"
                onClick={() => onInspect(n.open!)}
              >
                {n.label}
              </button>
            ) : (
              <span
                className={n.kind === 'answer' ? 'whitespace-pre-wrap text-text-1' : 'text-text-1'}
              >
                {n.label}
              </span>
            )}
            {n.state ? (
              <span className={`text-[14px] ${TONE_CLASS[n.tone ?? 'idle']}`}>{n.state}</span>
            ) : null}
          </div>
          {n.detail ? <span className="text-[14px] text-text-2">{n.detail}</span> : null}
          {n.children.length ? <Tree nodes={n.children} onInspect={onInspect} /> : null}
        </li>
      ))}
    </ul>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="grid gap-2" aria-label={title}>
      <h3 className="m-0 text-[14px] font-semibold text-text-2">{title}</h3>
      {children}
    </section>
  )
}

function Controls({
  bark,
  store,
  initialPanel,
  ctx,
}: {
  bark: BarkRecord
  store: BarkStore
  initialPanel?: 'redirect' | undefined
  ctx: AggContext
}) {
  const toast = useToast()
  const [panel, setPanel] = useState<'redirect' | 'cancel' | null>(initialPanel ?? null)
  const [other, setOther] = useState('')
  const [busy, setBusy] = useState(false)
  const act = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(true)
    try {
      await fn()
      toast(label)
      setPanel(null)
    } catch (e) {
      toast(errorText(e), 'error')
    } finally {
      setBusy(false)
    }
  }
  const started = bark.actions.flatMap((a) => a.ops.filter((o) => o.op === 'run.started')).length
  const created = bark.actions.flatMap((a) => a.ops.filter((o) => o.op === 'task.created')).length
  const steered = bark.actions.some(
    (a) => a.status === 'active' && a.ops.some((o) => o.op === 'run.steered'),
  )
  const terminal = bark.status === 'undone' || bark.status === 'cancelled'
  return (
    <div className="grid gap-2">
      <div className="flex flex-wrap gap-2">
        {canUndo(bark, Date.now()) ? (
          <Button
            size="sm"
            disabled={busy}
            onClick={() =>
              void act(steered ? 'Undone — the run was told to disregard it' : 'Undone', () =>
                store.control(bark, 'undo'),
              )
            }
          >
            Undo
          </Button>
        ) : null}
        <Button
          size="sm"
          disabled={busy || terminal}
          aria-expanded={panel === 'redirect'}
          onClick={() => setPanel(panel === 'redirect' ? null : 'redirect')}
        >
          Redirect
        </Button>
        <Button
          size="sm"
          disabled={busy || terminal}
          onClick={() => void act('Sorting it again', () => store.control(bark, 'retriage'))}
        >
          Re-triage
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={busy || terminal}
          aria-expanded={panel === 'cancel'}
          onClick={() => setPanel(panel === 'cancel' ? null : 'cancel')}
        >
          Cancel
        </Button>
      </div>
      {panel === 'redirect' ? (
        <div className="grid gap-2 rounded-lg bg-surface-2 p-3" data-redirect>
          <p className="m-0 text-[14px] text-text-2">
            Pick another home and it moves, along with anything it started.
          </p>
          <ul className="m-0 grid list-none gap-0.5 p-0 text-[14px]" data-redirect-preview>
            {redirectPreview(bark, ctx).map((l) => (
              <li
                key={l.text}
                className={
                  l.tone === 'keep'
                    ? 'text-status-wait'
                    : l.tone === 'move'
                      ? 'text-text-1'
                      : 'text-text-2'
                }
              >
                {l.tone === 'keep' ? 'Stays: ' : l.tone === 'move' ? 'Moves: ' : ''}
                {l.text}
              </li>
            ))}
          </ul>
          {REDIRECTS.map(([label, sub]) => (
            <button
              key={label}
              type="button"
              className="grid rounded-lg bg-surface-1 px-3 py-2 text-left"
              disabled={busy}
              onClick={() =>
                void act('Redirecting', () =>
                  store.control(bark, 'redirect', { instruction: label }),
                )
              }
            >
              <b className="text-[15px] text-text-1">{label}</b>
              <span className="text-[14px] text-text-2">{sub}</span>
            </button>
          ))}
          <label className="grid gap-1 text-[14px] text-text-2">
            Somewhere else
            <input
              value={other}
              onChange={(e) => setOther(e.target.value)}
              className="rounded-md border border-border-subtle bg-surface-1 px-2 py-1 text-[15px] text-text-1"
              placeholder="Say where it should go"
            />
          </label>
          <Button
            size="sm"
            variant="primary"
            disabled={busy || !other.trim()}
            onClick={() =>
              void act('Redirecting', () =>
                store.control(bark, 'redirect', { instruction: other.trim() }),
              )
            }
          >
            Redirect
          </Button>
        </div>
      ) : null}
      {panel === 'cancel' ? (
        <div
          className="grid gap-2 rounded-lg bg-status-wait-soft p-3"
          role="group"
          aria-label="Confirm cancel"
        >
          <p className="m-0 text-[14px] text-text-1">
            This stops {started} run{started === 1 ? '' : 's'} this started and cancels its
            unstarted tasks ({created} created). Anything already landed stays.
          </p>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="danger"
              disabled={busy}
              onClick={() => void act('Cancelled', () => store.control(bark, 'cancel'))}
            >
              Cancel it
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setPanel(null)}>
              Keep it
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export function BarkInspector({ projectId, barkId, onInspect, initialPanel }: BarkInspectorProps) {
  const { bark, store, isLoading, data: all } = useBark(projectId, barkId)
  // segmentation: "This belongs to…" targets (recent live items of this project).
  const items = useMemo(
    () =>
      (all ?? [])
        .filter((b) => !['cancelled', 'undone'].includes(b.status))
        .sort((a, b) => b.createdAt - a.createdAt)
        .slice(0, 30)
        .map((b) => ({ id: b.id, title: b.title ?? b.text.slice(0, 80) })),
    [all],
  )
  const ctx = useAggContext(projectId)
  const agg = useMemo(() => (bark ? barkAggregate(bark, ctx) : undefined), [bark, ctx])
  const tree = useMemo(() => (bark ? barkTree(bark, ctx) : []), [bark, ctx])
  if (!bark || !store || !agg)
    return (
      <p className="m-0 text-[14px] text-text-3">
        {isLoading ? 'Opening…' : 'This item is not available.'}
      </p>
    )
  const triageRun = bark.triage.itemRunId ?? bark.triage.routerRunId
  const lastDecision = bark.actions.at(-1)
  return (
    <article className="grid gap-5" aria-labelledby={`bark-h-${bark.id}`}>
      <header className="grid gap-1">
        <div className="flex items-center gap-2">
          <BarkGlyph glyph={agg.glyph} />
          <span className="text-[14px] text-text-2">{BARK_GLYPH_LABEL[agg.glyph]}</span>
        </div>
        <h2
          id={`bark-h-${bark.id}`}
          tabIndex={-1}
          className="m-0 text-[17px] font-semibold text-text-1"
        >
          {bark.title ?? agg.line}
        </h2>
        <p className={`m-0 text-[14px] ${agg.needsYou ? 'text-status-wait' : 'text-text-2'}`}>
          {agg.line}
        </p>
      </header>

      <Section title="You said">
        <blockquote className="m-0 whitespace-pre-wrap border-l-2 border-border-subtle pl-3 text-[15px] text-text-1">
          {bark.text || '(attachments only)'}
        </blockquote>
        <p className="m-0 text-[14px] text-text-3">
          <Ago ts={bark.createdAt} /> · from {bark.source === 'tab' ? 'this tab' : bark.source}
          {bark.dictated ? ' · dictated' : ''}
          {bark.attachments.length
            ? ` · ${bark.attachments.length} attachment${bark.attachments.length === 1 ? '' : 's'}`
            : ''}
        </p>
      </Section>

      <Section title="Viko understood">
        {bark.interpretation ? (
          <p className="m-0 text-[15px] text-text-1">{bark.interpretation}</p>
        ) : (
          <p className="m-0 text-[14px] text-text-3">
            {bark.status === 'queued' || bark.status === 'triaging'
              ? 'Working out where this goes…'
              : 'No interpretation yet.'}
          </p>
        )}
        {lastDecision ? (
          <p className="m-0 text-[14px] text-text-2">
            Decision: <b>{lastDecision.decision}</b>
            {lastDecision.assumptions.length
              ? ` · assumed ${lastDecision.assumptions.map((a) => a.text).join('; ')}`
              : ''}
          </p>
        ) : null}
        {triageRun ? (
          <button
            type="button"
            className="justify-self-start text-[14px] text-accent-text"
            onClick={() => onInspect(`run:${triageRun}`)}
          >
            Open triage conversation
          </button>
        ) : null}
      </Section>

      <Section title="Actions">
        {tree.length ? (
          <Tree nodes={tree} onInspect={onInspect} />
        ) : (
          <p className="m-0 text-[14px] text-text-3">Nothing has been done yet.</p>
        )}
      </Section>

      <Conversation
        bark={bark}
        store={store}
        onOpenItem={(id) => onInspect(`bark:${id}`)}
        collapsed={bark.mode === 'quick' && bark.status !== 'discussing'}
        items={items}
      />

      <Section title="Controls">
        <Controls bark={bark} store={store} initialPanel={initialPanel} ctx={ctx} />
      </Section>

      <Section title="History">
        <RetriageDiff bark={bark} ctx={ctx} />
        <ol className="m-0 grid list-none gap-1 p-0 text-[14px] text-text-2" data-bark-timeline>
          {historyTimeline(bark).map((h, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: append-only log
            <li key={i} className={h.tone === 'fail' ? 'text-status-fail' : undefined}>
              <Ago ts={h.at} /> · {h.text} · {h.who}
            </li>
          ))}
        </ol>
      </Section>
    </article>
  )
}

/** outcome-polish: what the latest redirect / re-triage replaced (history keeps both). */
function RetriageDiff({ bark, ctx }: { bark: BarkRecord; ctx: AggContext }) {
  const d = retriageDiff(bark, ctx)
  if (!d || (!d.removed.length && !d.added.length)) return null
  return (
    <div className="mb-2 grid gap-1 rounded-lg bg-surface-2 p-3 text-[14px]" data-retriage-diff>
      <b className="text-text-1">{d.cause}</b>
      {d.removed.map((x) => (
        <span key={`-${x}`} className="text-text-2">
          − {x}
        </span>
      ))}
      {d.added.map((x) => (
        <span key={`+${x}`} className="text-text-1">
          + {x}
        </span>
      ))}
    </div>
  )
}
